import { FC, useEffect, useMemo, useState } from 'react';
import styles from './styles.module.scss';
import Input from '@components/common/input';
import Button from '@components/common/button';
import { useTranslation } from 'react-i18next';
import { useLocales } from '@hooks/helpers/useLocales';
import { apiPayBillService } from '@api';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { TCreatePayee, TPayee, TSearchedPayees } from '@xeppt/xeppt-sdk/types';
import { useDebounceValue } from 'usehooks-ts';
import { highlightSearchText } from '@utils/index';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { Icon } from '@components/icons';
import { routes } from '@const/routes';
import { useNavigate } from 'react-router-dom';

interface IProps {
    handleBack: () => void;
    refetchPayees: () => void;
    chosenPayee?: string | null;
    payeesList: TPayee[];
}

const defaultPayee = {
    alias: '',
    account_number: '',
    name: '',
    code: ''
};

const Payee: FC<IProps> = ({ handleBack, chosenPayee, payeesList, refetchPayees }) => {
    const { t } = useTranslation('sections', {
        keyPrefix: 'pay_bills.manage_payees'
    });
    const [data, setData] = useState(defaultPayee);
    const [search, setSearch] = useState('');
    const [requestSearch, setRequestSearch] = useDebounceValue('', 500);
    const [chosenSearch, setChosenSearch] = useState('');
    const {
        labelLocale,
        submitLocale,
        requestErrorLocale,
        requestSuccessLocale,
        validationLocale,
        placeholderLocale
    } = useLocales();
    const [isError, setIsError] = useState(false);
    const navigate = useNavigate();

    const { data: chosenPayeeData } = useApiQuery({
        method: () => apiPayBillService.getPayee(chosenPayee as string),
        condition: !!chosenPayee,
        deps: [chosenPayee]
    });

    useEffect(() => {
        if (chosenPayeeData) {
            setData({
                account_number: chosenPayeeData.account_number,
                alias: chosenPayeeData.alias || '',
                code: chosenPayeeData.code,
                name: chosenPayeeData.name
            });
            setChosenSearch(chosenPayeeData.name);
        }
    }, [chosenPayeeData]);

    const { handleRequest: handleUpdatePayee, isLoading: isUpdatePayeeLoading } = useApiMutation({
        method: () => {
            return apiPayBillService
                .updatePayee(chosenPayee as string, data as TCreatePayee)
                .then()
                .catch();
        },
        onSuccess: () => {
            handleBack();
            requestSuccessLocale('update_payee');
            navigate(routes.pay_bills_payees);
            refetchPayees();
        },
        onError: requestErrorLocale
    });

    const { handleRequest: handleCreatePayee, isLoading: isCreatePayeeLoading } = useApiMutation({
        method: () => {
            return apiPayBillService.createPayee({
                ...data,
                ...(payeesList.find((item) => item.code === chosenPayee) as TSearchedPayees)
            });
        },
        onSuccess: () => {
            setData(defaultPayee);
            handleBack();
            requestSuccessLocale('create_payee');
            refetchPayees();
        },
        onError: requestErrorLocale
    });

    const { data: searchedPayees, handleRequest: handleSearchPayee } = useApiMutation({
        method: () => apiPayBillService.searchPayees(search || '')
    });

    useEffect(() => {
        setRequestSearch(search);
    }, [search]);

    useEffect(() => {
        if (requestSearch.length >= 3) {
            handleSearchPayee(undefined);
        }
    }, [requestSearch]);

    const handleAddPayee = () => {
        if (data.account_number) {
            setIsError(false);
            if (chosenPayee && data.account_number) {
                handleUpdatePayee(undefined);
            }
            if (!chosenPayee) {
                handleCreatePayee(undefined);
            }
        } else {
            setIsError(true);
        }
    };

    const existingPayees = useMemo(() => {
        return payeesList.filter((item) => item.name.toLowerCase().includes(search.toLowerCase()));
    }, [search, payeesList]);

    return (
        <motion.div {...pageAnimation} className={styles.add_content}>
            <div className={styles.search_wrapper}>
                {!chosenPayee && (
                    <p className={styles.description}>
                        To add a new payee, please find it in the payees list and fill out the
                        details
                    </p>
                )}
                {!chosenPayee && (
                    <Input
                        full
                        leftIcon="search"
                        value={search || chosenSearch}
                        rightIcon={search.length > 0 ? 'filed_close' : undefined}
                        onClickRightIcon={() => setSearch('')}
                        onChange={(val) => {
                            setSearch(val);
                            if (chosenSearch) {
                                setChosenSearch('');
                            }
                        }}
                        placeholder={placeholderLocale('search_payee_name')}
                    />
                )}
                {chosenPayee && (
                    <div className={styles.chosen_payee}>
                        <div>{data.name}</div>
                        <Icon name="check" />
                    </div>
                )}
                {search && !chosenSearch && (
                    <div className={styles.results_wrapper}>
                        {searchedPayees && searchedPayees.length > 0 && (
                            <div className={styles.result}>
                                {searchedPayees?.map((item) => {
                                    return (
                                        <div
                                            onClick={() => {
                                                setData((state) => ({ ...state, ...item }));
                                                setChosenSearch(item.name);
                                                setSearch('');
                                            }}
                                            className={styles.title}
                                            key={item.code}
                                            dangerouslySetInnerHTML={{
                                                __html: highlightSearchText(item.name, search)
                                            }}
                                        />
                                    );
                                })}
                            </div>
                        )}
                        {existingPayees.length > 0 && (
                            <div className={styles.result}>
                                <div className={styles.result_length}>Existing payees</div>
                                {existingPayees.map((item) => {
                                    return (
                                        <div
                                            className={styles.title}
                                            key={item.code}
                                            dangerouslySetInnerHTML={{
                                                __html: highlightSearchText(item.name, search)
                                            }}
                                        />
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
            {search.length === 0 && (
                <>
                    <Input
                        full
                        label={labelLocale('account_number')}
                        helperText={
                            isError ? validationLocale('account_number') : t('help_account')
                        }
                        onlyNumbers
                        placeholder={placeholderLocale('enter_account_number')}
                        value={data.account_number}
                        className={styles.data_input}
                        onChange={(val) => setData((state) => ({ ...state, account_number: val }))}
                        error={isError}
                    />
                    <Input
                        full
                        label={t('name_label')}
                        helperText={t('helper_name')}
                        placeholder={placeholderLocale('enter_payee_nickname')}
                        value={data.alias}
                        className={styles.data_input}
                        onChange={(val) => setData((state) => ({ ...state, alias: val }))}
                    />
                    <Button
                        variant="primary"
                        onClick={handleAddPayee}
                        className={styles.submit}
                        isLoading={isCreatePayeeLoading || isUpdatePayeeLoading}>
                        {submitLocale(chosenPayee ? 'save_changes' : 'add_payee')}
                    </Button>
                </>
            )}
        </motion.div>
    );
};

export default Payee;
